import { z } from 'zod';
import { ChatNavigateRoute, UiAction } from './chat-actions.js';
import {
  AnomalySeverity,
  AnomalyType,
  BusinessStatus,
  ConflictType,
  RuntimeStatus,
  WoSignalType,
  WoWarningType,
} from './enums.js';
import { IsoDateTime, UuidString } from './primitives.js';

// ─── RunEvent (SSE, 9 типов, discriminated union) ────────────────────

const RunEventBase = {
  run_id: UuidString,
  at: IsoDateTime,
  seq: z.number().int().nonnegative(),
};

export const RunStartedEvent = z
  .object({
    kind: z.literal('run.started'),
    ...RunEventBase,
    task_type: z.string(),
  })
  .passthrough();

export const RunStatusChangedEvent = z
  .object({
    kind: z.literal('run.status_changed'),
    ...RunEventBase,
    runtime_status: RuntimeStatus,
    business_status: BusinessStatus.optional(),
  })
  .passthrough();

export const RunStepEvent = z
  .object({
    kind: z.literal('run.step'),
    ...RunEventBase,
    step_name: z.string(),
    message: z.string().optional(),
  })
  .passthrough();

export const RunLlmTokensEvent = z
  .object({
    kind: z.literal('run.llm_tokens'),
    ...RunEventBase,
    delta: z.string(),
  })
  .passthrough();

export const RunToolCallEvent = z
  .object({
    kind: z.literal('run.tool_call'),
    ...RunEventBase,
    tool_name: z.string(),
    status: z.enum(['started', 'ok', 'failed']),
    duration_ms: z.number().int().optional(),
  })
  .passthrough();

export const RunConflictAddedEvent = z
  .object({
    kind: z.literal('run.conflict_added'),
    ...RunEventBase,
    conflict_id: UuidString,
    conflict_type: ConflictType,
  })
  .passthrough();

export const RunAnomalyAddedEvent = z
  .object({
    kind: z.literal('run.anomaly_added'),
    ...RunEventBase,
    anomaly_id: UuidString,
    anomaly_type: AnomalyType,
    severity: AnomalySeverity,
  })
  .passthrough();

export const RunApprovalRequestedEvent = z
  .object({
    kind: z.literal('run.approval_requested'),
    ...RunEventBase,
    approval_id: UuidString,
    tier: z.number().int().min(0).max(3),
  })
  .passthrough();

export const RunCompletedEvent = z
  .object({
    kind: z.literal('run.completed'),
    ...RunEventBase,
    runtime_status: RuntimeStatus,
    business_status: BusinessStatus,
  })
  .passthrough();

export const RunEvent = z.discriminatedUnion('kind', [
  RunStartedEvent,
  RunStatusChangedEvent,
  RunStepEvent,
  RunLlmTokensEvent,
  RunToolCallEvent,
  RunConflictAddedEvent,
  RunAnomalyAddedEvent,
  RunApprovalRequestedEvent,
  RunCompletedEvent,
]);
export type RunEvent = z.infer<typeof RunEvent>;

// ─── WoEvent (Work Order lifecycle SSE) ──────────────────────────────

export const WoSignalAcceptedEvent = z
  .object({
    kind: z.literal('wo.signal_accepted'),
    work_order_id: UuidString,
    signal: WoSignalType,
    at: IsoDateTime,
  })
  .passthrough();

export const WoStatusChangedEvent = z
  .object({
    kind: z.literal('wo.status_changed'),
    work_order_id: UuidString,
    from: z.string(),
    to: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

export const WoJobCardUpdatedEvent = z
  .object({
    kind: z.literal('wo.job_card_updated'),
    work_order_id: UuidString,
    job_card_id: UuidString,
    at: IsoDateTime,
  })
  .passthrough();

/** Signal received by workflow (до применения эффекта — для UI activity-log). */
export const WoSignalReceivedEvent = z
  .object({
    kind: z.literal('wo.signal_received'),
    work_order_id: UuidString,
    signal: WoSignalType,
    actor_user_id: UuidString.nullable(),
    at: IsoDateTime,
  })
  .passthrough();

/** Job card добавлена / обновлена во время in_progress. */
export const WoJobCardAddedEvent = z
  .object({
    kind: z.literal('wo.job_card_added'),
    work_order_id: UuidString,
    job_card_id: UuidString,
    operation_name: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

/** SLE сгенерирован workflow (auto-manufacture flow). */
export const WoSleGeneratedEvent = z
  .object({
    kind: z.literal('wo.sle_generated'),
    work_order_id: UuidString,
    stock_entry_id: UuidString,
    stock_entry_type: z.string(),
    sle_count: z.number().int().nonnegative(),
    at: IsoDateTime,
  })
  .passthrough();

/** Work Order завершён (после complete + FG receipt). */
export const WoCompletedEvent = z
  .object({
    kind: z.literal('wo.completed'),
    work_order_id: UuidString,
    qty_produced: z.string(),
    qty_scrapped: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

/**
 * PLM-2 (пак `tails-closeout`, B14) — фактическая себестоимость заказа не записана.
 * Заказ всё равно завершён (расчёт non-fatal), а на нём стоит отметка
 * `variance_reason = 'actual_cost_failed'`.
 */
export const WoActualCostFailedEvent = z
  .object({
    kind: z.literal('wo.actual_cost_failed'),
    work_order_id: UuidString,
    at: IsoDateTime,
  })
  .passthrough();

/** Work Order отменён. */
export const WoCancelledEvent = z
  .object({
    kind: z.literal('wo.cancelled'),
    work_order_id: UuidString,
    reason: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

/**
 * Warning raised (wo-warning-detector).
 * B-A.6.1 — structural-only (никакого `message`): FE не рендерит event напрямую,
 * invalidate-ит bundle query, получает денормализованные items/batches и рендерит через i18n.
 */
export const WoWarningRaisedEvent = z
  .object({
    kind: z.literal('wo.warning_raised'),
    work_order_id: UuidString,
    warning_type: WoWarningType,
    severity: AnomalySeverity,
    related_item_id: UuidString.nullable().optional(),
    related_batch_id: UuidString.nullable().optional(),
    qty_needed: z.string().nullable().optional(),
    qty_available: z.string().nullable().optional(),
    uom_code: z.string().nullable().optional(),
    at: IsoDateTime,
  })
  .strict();

export const WoEvent = z.discriminatedUnion('kind', [
  WoSignalAcceptedEvent,
  WoStatusChangedEvent,
  WoJobCardUpdatedEvent,
  WoSignalReceivedEvent,
  WoJobCardAddedEvent,
  WoSleGeneratedEvent,
  WoCompletedEvent,
  WoCancelledEvent,
  WoWarningRaisedEvent,
  WoActualCostFailedEvent,
]);
export type WoEvent = z.infer<typeof WoEvent>;

// ─── ProjectEvent (S15 / P15) — Project lifecycle SSE ─────────────────

const ProjectEventBase = {
  project_id: UuidString,
  at: IsoDateTime,
  event_id: z.string().optional(),
  actor_user_id: UuidString.nullable().optional(),
};

export const ProjectTaskCreatedEvent = z
  .object({
    kind: z.literal('project.task.created'),
    task_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectTaskUpdatedEvent = z
  .object({
    kind: z.literal('project.task.updated'),
    task_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectTaskDeletedEvent = z
  .object({
    kind: z.literal('project.task.deleted'),
    task_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectTaskReorderedEvent = z
  .object({
    kind: z.literal('project.task.reordered'),
    task_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectTaskStatusChangedEvent = z
  .object({
    kind: z.literal('project.task.status_changed'),
    task_id: UuidString,
    new_status: z.string(),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectMilestoneReachedEvent = z
  .object({
    kind: z.literal('project.milestone.reached'),
    milestone_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectMilestoneInvoiceDraftedEvent = z
  .object({
    kind: z.literal('project.milestone.invoice_drafted'),
    milestone_id: UuidString,
    invoice_id: UuidString,
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectStageTransitionedEvent = z
  .object({
    kind: z.literal('project.stage.transitioned'),
    from: z.string(),
    to: z.string(),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectCancelledEvent = z
  .object({
    kind: z.literal('project.cancelled'),
    from_stage: z.string(),
    reason: z.string(),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectHealthUpdatedEvent = z
  .object({
    kind: z.literal('project.health.updated'),
    new_health: z.string(),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectMetricsRecomputedEvent = z
  .object({
    kind: z.literal('project.metrics.recomputed'),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectTaskLogChangedEvent = z
  .object({
    kind: z.literal('project.task_log.changed'),
    task_log_id: UuidString,
    action: z.enum(['created', 'updated', 'deleted']),
    ...ProjectEventBase,
  })
  .passthrough();

export const ProjectEvent = z.discriminatedUnion('kind', [
  ProjectTaskCreatedEvent,
  ProjectTaskUpdatedEvent,
  ProjectTaskDeletedEvent,
  ProjectTaskReorderedEvent,
  ProjectTaskStatusChangedEvent,
  ProjectMilestoneReachedEvent,
  ProjectMilestoneInvoiceDraftedEvent,
  ProjectStageTransitionedEvent,
  ProjectCancelledEvent,
  ProjectHealthUpdatedEvent,
  ProjectMetricsRecomputedEvent,
  ProjectTaskLogChangedEvent,
]);
export type ProjectEvent = z.infer<typeof ProjectEvent>;

// ─── ExcelOverrideEvent ───────────────────────────────────────────────

export const ExcelOverrideDetectedEvent = z
  .object({
    kind: z.literal('excel_override.detected'),
    upload_id: UuidString,
    sheet: z.string(),
    cell: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

export const ExcelOverrideResolvedEvent = z
  .object({
    kind: z.literal('excel_override.resolved'),
    upload_id: UuidString,
    resolution: z.enum(['accepted', 'rejected', 'deferred']),
    at: IsoDateTime,
  })
  .passthrough();

export const ExcelOverrideChangeDecidedEvent = z
  .object({
    kind: z.literal('excel_override.change_decided'),
    upload_id: UuidString,
    change_id: UuidString,
    decision: z.enum(['accept', 'defer', 'reject']),
    at: IsoDateTime,
  })
  .passthrough();

export const ExcelOverrideBulkDecidedEvent = z
  .object({
    kind: z.literal('excel_override.bulk_decided'),
    upload_id: UuidString,
    accepted: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    at: IsoDateTime,
  })
  .passthrough();

export const ExcelOverrideFinalizedEvent = z
  .object({
    kind: z.literal('excel_override.finalized'),
    upload_id: UuidString,
    conflicts_created: z.number().int().nonnegative(),
    at: IsoDateTime,
  })
  .passthrough();

export const ExcelOverrideEvent = z.discriminatedUnion('kind', [
  ExcelOverrideDetectedEvent,
  ExcelOverrideResolvedEvent,
  ExcelOverrideChangeDecidedEvent,
  ExcelOverrideBulkDecidedEvent,
  ExcelOverrideFinalizedEvent,
]);
export type ExcelOverrideEvent = z.infer<typeof ExcelOverrideEvent>;

// ─── Chat SSE events (streaming NLQ) ─────────────────────────────────

export const ChatMessageDeltaEvent = z
  .object({
    kind: z.literal('chat.message_delta'),
    conversation_id: UuidString,
    delta: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

export const ChatToolCallEvent = z
  .object({
    kind: z.literal('chat.tool_call'),
    conversation_id: UuidString,
    tool_name: z.string(),
    args: z.record(z.unknown()),
    at: IsoDateTime,
  })
  .passthrough();

export const ChatToolResultEvent = z
  .object({
    kind: z.literal('chat.tool_result'),
    conversation_id: UuidString,
    tool_name: z.string(),
    ok: z.boolean(),
    /** CIA-F19 (пак `tails-closeout`, F6) — код отказа шага из result-объекта `{error}`. */
    error_code: z.string().optional(),
    preview: z.string().optional(),
    /**
     * Круг 2 кадра 2 гейта захода С2, UX-1 (дословно): «Число ответа не выводится
     * из ленты — шаги отчитываются „прочитаны / выполнен“ с аргументами, но без
     * возвращённого объёма. Заголовочные 727 не встречаются нигде, кроме прозы
     * агента».
     *
     * Структурное число, не текст: сколько строк инструмент вернул. Подпись и
     * склонение — на стороне UI (`@staiter/i18n`).
     */
    result_count: z.number().int().nonnegative().optional(),
    at: IsoDateTime,
  })
  .passthrough();

/**
 * CIA-F25 / пак `tails-closeout` B11 — чем закончился ход агента. Структурный код,
 * а не текст: подпись и действие («Повторить») решает UI через `@staiter/i18n`.
 *
 *  - `answered` — есть текст ответа или карточка действия;
 *  - `no_answer` — провайдер завершил ход штатно, но без текста и без действия;
 *  - `provider_error` — провайдер завершил ход с `finish_reason: error`
 *    (до правки фронт рисовал пустой пузырь агента — Д-10 деплоя 07.09);
 *  - `tool_budget_exhausted` — в ходе исчерпан лимит вызовов инструментов;
 *  - `refused` — модель отказала в ответе (классификатор безопасности провайдера,
 *    `finish_reason: content-filter`; пак `llm-haiku-5-5`, B4). Запасной модели на стороне
 *    сервера у Haiku 5.5 нет — ход кончается отказом, и его надо назвать, а не выдать за пустоту;
 *  - `truncated` — ответ оборвался на лимите ответа (`finish_reason: length`; LH-B9 пака
 *    `llm-haiku-5-5`): текст неполный, в историю модели как ответ не идёт;
 *  - `aborted` — клиент оборвал поток до конца хода.
 *
 * Копия набора — в `legacy-ui-contracts.ts` (`message_completed.outcome`), перечислена там же,
 * а не импортирована: цикл импортов. Менять оба места вместе.
 */
export const ChatTurnOutcome = z.enum([
  'answered',
  'no_answer',
  'provider_error',
  'tool_budget_exhausted',
  'refused',
  'truncated',
  'aborted',
]);
export type ChatTurnOutcome = z.infer<typeof ChatTurnOutcome>;

/**
 * Пак `llm-haiku-5-5`, Л-4 Р.2 (`LH-B27`, `LH-F15`) — исходы хода без ответа, при которых строка
 * ассистента пишется с маркером `turn_failed {outcome}`: из истории — та же запись провала хода,
 * что живьём (`turn_aborted{outcome}`). Значение исхода, а не код ошибки: коды чата в `ErrorCode` не
 * входят.
 */
export const FAILED_TURN_OUTCOMES = [
  'provider_error',
  'no_answer',
  'tool_budget_exhausted',
] as const satisfies readonly ChatTurnOutcome[];
export type FailedTurnOutcome = (typeof FAILED_TURN_OUTCOMES)[number];

export const ChatFinalEvent = z
  .object({
    kind: z.literal('chat.final'),
    conversation_id: UuidString,
    message_id: UuidString,
    at: IsoDateTime,
    /** Необязательное на один релиз — старые кадры его не несли (B11). */
    outcome: ChatTurnOutcome.optional(),
  })
  .passthrough();

/**
 * chat-interactive-actions B2 [Б15] — `message` стал необязательным: бэкенд с
 * E-BE.5 шлёт только структурный `code` (`chat.ts`), а обязательное поле делало
 * контракт ложным — каждый реальный фрейм его не проходил. Целевое состояние —
 * выпилить `message` совсем (i18n-инвариант: UI локализует по `code`); оставлен
 * optional на один релиз ради старых клиентов, которые его читают.
 *
 * Известные `code`: `server.internal`, `steps_exhausted` (B8), `tool_budget_exhausted`
 * (B17), `invalid_tool_arguments` (B8); пак `llm-haiku-5-5`, B5 — ошибки провайдера модели:
 * `provider_rate_limited` (429), `provider_overloaded` (529), `provider_auth` (ключ),
 * `provider_billing` (баланс и лимит расходов), `provider_config` (модель или параметр отвергнуты,
 * LH-B11). `details` — структурные параметры кода.
 */
export const ChatErrorEvent = z
  .object({
    kind: z.literal('chat.error'),
    conversation_id: UuidString.optional(),
    code: z.string(),
    message: z.string().optional(),
    details: z.record(z.unknown()).optional(),
    at: IsoDateTime,
  })
  .passthrough();

/**
 * chat-interactive-actions B2 (SOLUTION §2.4) — «чат → действие в UI».
 *
 * Эмитится из stream-цикла `chat.ts` по `tool-result` инструментов
 * `apply_task_filter` / `propose_task_bulk` (не из execute — у него нет ручки
 * стрима, §8). БЕЗ `message_id`: сообщения ассистента ещё не существует в момент
 * эмита [Б12]; привязка — `conversation_id` + `draft_id` (черновик уже в БД, I7).
 * Strict: payload валидируется ДО эмита (I4), лишних полей быть не может.
 */
export const ChatUiActionEvent = z
  .object({
    kind: z.literal('chat.ui_action'),
    conversation_id: UuidString,
    draft_id: UuidString.optional(),
    action: UiAction,
    at: IsoDateTime,
  })
  .strict();
export type ChatUiActionEvent = z.infer<typeof ChatUiActionEvent>;

/**
 * chat-interactive-actions B2/B9 (SOLUTION §5) — hard-refusal до вызова модели.
 * Canonical-вариант мёртвой legacy-ветки `write_intent_refused`; `suggested_route`
 * — строго whitelist-маршрут, фронт переходит SPA-навигацией (F5).
 */
export const ChatWriteIntentRefusedEvent = z
  .object({
    kind: z.literal('chat.write_intent_refused'),
    conversation_id: UuidString,
    suggested_route: ChatNavigateRoute.optional(),
    at: IsoDateTime,
  })
  .strict();
export type ChatWriteIntentRefusedEvent = z.infer<typeof ChatWriteIntentRefusedEvent>;

export const ChatEvent = z.discriminatedUnion('kind', [
  ChatMessageDeltaEvent,
  ChatToolCallEvent,
  ChatToolResultEvent,
  ChatFinalEvent,
  ChatErrorEvent,
  ChatUiActionEvent,
  ChatWriteIntentRefusedEvent,
]);
export type ChatEvent = z.infer<typeof ChatEvent>;

// ─── UserExportEvent ──────────────────────────────────────────────────

export const UserExportProgressEvent = z
  .object({
    kind: z.literal('user_export.progress'),
    job_id: UuidString,
    progress: z.number().int().min(0).max(100),
    at: IsoDateTime,
  })
  .passthrough();

export const UserExportReadyEvent = z
  .object({
    kind: z.literal('user_export.ready'),
    job_id: UuidString,
    result_url: z.string().url(),
    at: IsoDateTime,
  })
  .passthrough();

export const UserExportFailedEvent = z
  .object({
    kind: z.literal('user_export.failed'),
    job_id: UuidString,
    error_code: z.string(),
    message: z.string(),
    at: IsoDateTime,
  })
  .passthrough();

export const UserExportEvent = z.discriminatedUnion('kind', [
  UserExportProgressEvent,
  UserExportReadyEvent,
  UserExportFailedEvent,
]);
export type UserExportEvent = z.infer<typeof UserExportEvent>;
